import { css } from '@emotion/react';
import { useEffect, useState, FC } from 'react';
import { useTranslation } from 'react-i18next';
import SectionHeading from '../SectionHeading';
import HoldingSymbolCard from '../../HoldingSymbolCard';
import { spacing, colors, fontStyles } from '../../../styles';
import { reportError } from '../../../utils/reporting';
import { getHoldings, Holding } from '../../../apis/backend';

interface Props {
  symbol: string;
}

const INITIAL_VISIBLE_COUNT = 5;

const styles = {
  card: css`
    margin-bottom: ${spacing.centi};
  `,
  seeMoreButton: css`
    ${fontStyles.cat};
    color: ${colors.petrolBlue};
    background: none;
    border: none;
    padding: ${spacing.regular} 0;
    cursor: pointer;
    width: 100%;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${spacing.milli};

    &:hover {
      opacity: 0.8;
    }

    html[data-theme='dark'] & {
      color: ${colors.skyBlue};
    }
  `,
};

const Holdings: FC<Props> = ({ symbol }) => {
  const { t } = useTranslation();
  const [holdings, setHoldings] = useState<Holding[]>();
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setHoldings(await getHoldings(symbol));
      } catch (e) {
        if (e instanceof Error) {
          reportError(e);
        } else {
          reportError(new Error(String(e)));
        }
      }
    })();
  }, [symbol]);

  if (!holdings || holdings.length < 1) return <div />;

  const visibleHoldings = isExpanded ? holdings : holdings.slice(0, INITIAL_VISIBLE_COUNT);
  const remainingCount = holdings.length - INITIAL_VISIBLE_COUNT;
  const shouldShowToggle = holdings.length > INITIAL_VISIBLE_COUNT;

  return (
    <div>
      <SectionHeading text={t('symbol.holdings')} />
      {visibleHoldings.map((holding: Holding) => (
        <HoldingSymbolCard key={holding.symbol} css={styles.card} {...holding} />
      ))}
      {shouldShowToggle && (
        <button css={styles.seeMoreButton} onClick={() => setIsExpanded(!isExpanded)}>
          {isExpanded ? <>{t('symbol.showLess')}</> : <>{t('symbol.seeMoreHoldings', { count: remainingCount })}</>}
        </button>
      )}
    </div>
  );
};

export default Holdings;
